function normalizeText(value) {
  return String(value || '').trim();
}

const CAPACITY_PREFIX = { reported: '', estimated: '约 ', mixed: '约 ', unavailable: '' };

export function formatUserContextCapacityLines(capacity) {
  const validCount = (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0;
  if (!validCount(capacity?.used_tokens)) {
    return ['会话上下文：暂无统计'];
  }
  const prefix = CAPACITY_PREFIX[capacity.source] ?? '';
  const used = capacity.used_tokens.toLocaleString('zh-CN');
  if (!validCount(capacity.limit_tokens) || capacity.limit_tokens === 0) {
    return [`会话上下文：${prefix}${used} Token · 上限未知`];
  }
  const remaining = validCount(capacity.remaining_tokens)
    ? `剩余容量：${prefix}${capacity.remaining_tokens.toLocaleString('zh-CN')} Token` : '';
  return [`会话上下文：${prefix}${used} / ${capacity.limit_tokens.toLocaleString('zh-CN')} Token`, remaining].filter(Boolean);
}


function coerceCount(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
}

function formatNumber(value) {
  return coerceCount(value).toLocaleString('zh-CN');
}

function resolveObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

export function buildContextLayerSummaryLabel(layers) {
  if (!Array.isArray(layers) || layers.length === 0) {
    return '';
  }
  const normalizedLayers = layers.filter((layer) => layer && typeof layer === 'object');
  if (normalizedLayers.length === 0) {
    return '';
  }
  const includedLayers = normalizedLayers.filter((layer) => layer.included !== false).length;
  return `上下文层 ${includedLayers}/${normalizedLayers.length}`;
}

export function resolveContextUsageSourceLabel(source) {
  const labels = {
    backend: '后端统计',
    stream_trace: '流式追踪',
    persisted_session: '已持久化上下文',
  };
  return labels[normalizeText(source)] || '本地估算';
}

export function resolveContextUsageProfileLabel(profile) {
  const labels = {
    light: '轻量窗口',
    medium: '标准窗口',
    heavy: '完整窗口',
  };
  return labels[normalizeText(profile)] || normalizeText(profile);
}

export function resolveContextUsageStatusLabel(status) {
  const labels = {
    normal: '占用正常',
    high: '占用较高',
    critical: '接近上限',
    over_limit: '已超上限',
  };
  return labels[normalizeText(status)] || normalizeText(status);
}

export function resolveContextUsageModeKeyLabel(modeKey) {
  const labels = {
    requirement_intake: '梳理模式',
    requirement: '梳理模式',
    requirement_canvas: '梳理模式',
    analysis: '分析模式',
    analysis_mode: '分析模式',
    sourcing: '寻源模式',
    intelligent_sourcing: '寻源模式',
  };
  return labels[normalizeText(modeKey)] || normalizeText(modeKey);
}

export function resolveContextUsageModeStateLabel(modeState) {
  const labels = {
    collecting: '收集中',
    running: '运行中',
    completed: '已完成',
    waiting_user: '等待用户',
  };
  return labels[normalizeText(modeState)] || normalizeText(modeState);
}

export function buildContextUsageBackendRouteLabel(modeKey, modeState) {
  const modeLabel = resolveContextUsageModeKeyLabel(modeKey);
  if (!modeLabel) {
    return '';
  }
  const stateLabel = resolveContextUsageModeStateLabel(modeState);
  return stateLabel ? `后端识别/提示 ${modeLabel} / ${stateLabel}` : `后端识别/提示 ${modeLabel}`;
}

export function buildContextStatsLabel(stats) {
  if (!stats) {
    return '';
  }
  const baseLabel = `语义沉淀 事实${stats.factsCount}/待确认${stats.unknownsCount}/证据${stats.evidenceCount}`;
  const turnLabel = stats.turnsCount > 0 ? ` · 轮次 ${stats.turnsCount}` : '';
  const artifactLabel = stats.artifactContextCount > 0 ? ` · 资料解读 ${stats.artifactContextCount}` : '';
  const graphLabel = stats.hasArchiveSummary
    ? ` · 图谱 节点${stats.graphNodesCount}/关系${stats.graphEdgesCount}`
    : '';
  return `${baseLabel}${turnLabel}${artifactLabel}${graphLabel}`;
}

export function formatTokenUsageLabel({ estimatedTokens, displayTokenLimit, tokenLimitSource }) {
  if (displayTokenLimit <= 0) {
    return `Token ${formatNumber(estimatedTokens)} / 未知`;
  }
  const limitLabel = tokenLimitSource === 'model' ? '模型上限' : '预算';
  return `Token ${formatNumber(estimatedTokens)} / ${formatNumber(displayTokenLimit)}（${limitLabel}）`;
}

export function formatContextBudgetLabel(budgetTokenLimit) {
  return budgetTokenLimit > 0 ? `上下文预算 ${formatNumber(budgetTokenLimit)}` : '';
}

function paramsFromActivity(record) {
  return {
    ...resolveObject(record?.counters),
    ...resolveObject(record?.detail_params),
    ...resolveObject(record?.headline_params),
  };
}

function activityHeadline(key, params) {
  if (key === 'context_activity.warning.headline') {
    return '上下文接近上限';
  }
  if (key === 'context_activity.syncing_canvas.headline') {
    return '正在同步 Canvas 上下文';
  }
  if (key === 'context_activity.ingesting_sources.headline') {
    return `正在理解 ${coerceCount(params.source_count || params.sources_total)} 份资料`;
  }
  if (key === 'context_activity.collecting.headline') {
    return `正在沉淀上下文：事实 ${coerceCount(params.facts)} / 待确认 ${coerceCount(params.unknowns)}`;
  }
  if (key === 'context_activity.idle.headline') {
    return '上下文已同步';
  }
  return '';
}

export function renderContextActivityHeadline(record) {
  const legacyHeadline = normalizeText(record?.headline);
  if (legacyHeadline) {
    return legacyHeadline;
  }
  const key = normalizeText(record?.headline_key);
  const params = paramsFromActivity(record);
  return activityHeadline(key, params);
}

export function renderContextActivityDetail(record) {
  const legacyDetail = normalizeText(record?.detail);
  if (legacyDetail) {
    return legacyDetail;
  }
  if (normalizeText(record?.detail_key) !== 'context_activity.counts.detail') {
    return '';
  }
  const params = paramsFromActivity(record);
  return [
    `事实 ${coerceCount(params.facts)}`,
    `待确认 ${coerceCount(params.unknowns)}`,
    `证据 ${coerceCount(params.evidence)}`,
    `资料解读 ${coerceCount(params.artifact_context)}`,
  ].join(' · ');
}

export function buildContextActivityLines(activity) {
  const record = resolveObject(activity);
  if (!Object.keys(record).length) {
    return [];
  }
  const headline = renderContextActivityHeadline(record);
  const detail = renderContextActivityDetail(record);
  return [
    headline ? `上下文活动 ${headline}` : '',
    detail,
  ].filter(Boolean);
}
